import { CampaignDescriptionUtils } from './CampaignDescriptionUtils';
import Quill from 'quill/core';
import QuillJs from 'quill';

const Parchment = QuillJs.import('parchment');
const Delta: any = QuillJs.import('delta');
const BlockEmbed: any = QuillJs.import('blots/block/embed');

export interface LinkDialogResult {
  title: string;
  value: string;
}

export type OpenLinkDialogFn = (initialTitle: string) => Promise<LinkDialogResult | null | undefined>;

interface IframeValue {
  src: string | null;
  style?: string | null;
  width?: string | null;
  height?: string | null;
  allow?: string | null;
  allowfullscreen?: string | null;
  frameborder?: string | null;
  referrerpolicy?: string | null;
  loading?: string | null;
  title?: string | null;
}

class IframeBlot extends BlockEmbed {
  static blotName = 'iframe';
  static tagName = 'IFRAME';

  static create(value: IframeValue | string): HTMLElement {
    const node = super.create() as HTMLElement;
    const allowedAttrs = ['src', 'style', 'width', 'height', 'allow', 'allowfullscreen', 'frameborder', 'referrerpolicy', 'loading', 'title'];
    const resolved: IframeValue = typeof value === 'string' ? { src: value } : value || { src: null };

    allowedAttrs.forEach(attr => {
      const attrValue = resolved[attr as keyof IframeValue];
      if (attrValue) {
        node.setAttribute(attr, attrValue as string);
      }
    });

    if (!node.hasAttribute('frameborder')) {
      node.setAttribute('frameborder', '0');
    }

    if (!node.hasAttribute('allowfullscreen')) {
      node.setAttribute('allowfullscreen', 'true');
    }

    return node;
  }

  static value(node: HTMLElement): IframeValue {
    const extract = (attribute: string) => node.getAttribute(attribute);
    return {
      src: extract('src'),
      style: extract('style'),
      width: extract('width'),
      height: extract('height'),
      allow: extract('allow'),
      allowfullscreen: extract('allowfullscreen'),
      frameborder: extract('frameborder'),
      referrerpolicy: extract('referrerpolicy'),
      loading: extract('loading'),
      title: extract('title'),
    };
  }
}

export class QuillConfigurator {
  private static initialized = false;

  static setup(): void {
    if (this.initialized) return;

    // Support for applying arbitrary inline and block styles
    const inlineStyleAttributor = new Parchment.Attributor('inlineStyle', 'style', {
      scope: Parchment.Scope.INLINE,
      whitelist: null,
    });
    const blockStyleAttributor = new Parchment.Attributor('blockStyle', 'style', {
      scope: Parchment.Scope.BLOCK,
      whitelist: null,
    });

    // Common element size style helpers (block scope)
    const elementWidth = new Parchment.Attributor('elementWidth', 'width', {
      scope: Parchment.Scope.BLOCK,
      whitelist: null,
    });
    const elementMinWidth = new Parchment.Attributor('elementMinWidth', 'min-width', {
      scope: Parchment.Scope.BLOCK,
      whitelist: null,
    });
    const elementHeight = new Parchment.Attributor('elementHeight', 'height', {
      scope: Parchment.Scope.BLOCK,
      whitelist: null,
    });
    const elementMinHeight = new Parchment.Attributor('elementMinHeight', 'min-height', {
      scope: Parchment.Scope.BLOCK,
      whitelist: null,
    });

    // Link target attributor (allow custom _newtab target)
    const linkTarget = new Parchment.Attributor('target', 'target', {
      scope: Parchment.Scope.INLINE,
      whitelist: ['_blank', '_self', '_parent', '_top', '_newtab'],
    });

    // Link ignore-inapp-browser attributor (boolean-like string)
    const ignoreInappBrowser = new Parchment.Attributor('ignore-inapp-browser', 'ignore-inapp-browser', {
      scope: Parchment.Scope.INLINE,
      whitelist: ['true', 'false'],
    });

    // Register formats
    QuillJs.register('formats/iframe', IframeBlot, true);
    QuillJs.register(
      {
        'formats/inlineStyle': inlineStyleAttributor,
        'formats/blockStyle': blockStyleAttributor,
        'formats/elementWidth': elementWidth,
        'formats/elementMinWidth': elementMinWidth,
        'formats/elementHeight': elementHeight,
        'formats/elementMinHeight': elementMinHeight,
        'formats/target': linkTarget,
        'formats/ignore-inapp-browser': ignoreInappBrowser,
      },
      true
    );

    this.initialized = true;
  }

  static getModules() {
    return {
      toolbar: CampaignDescriptionUtils.getToolbar(),
    };
  }

  static initEditor(editorInstance: any, opts: { openLinkDialog: OpenLinkDialogFn }): void {
    const toolbar = editorInstance.getModule('toolbar');
    const clipboard = editorInstance.getModule('clipboard');
    const quill = editorInstance;

    const applyInlineStyle = (delta: any, style: string | null) => {
      if (!style) return delta;
      const ops = (delta.ops || []).map((op: any) => {
        if (typeof op.insert !== 'string' || op.insert === '\n') return op;
        return { ...op, attributes: { ...(op.attributes || {}), inlineStyle: style } };
      });
      return new Delta(ops);
    };

    const applyBlockStyle = (delta: any, style: string | null) => {
      if (!style) return delta;
      const ops = (delta.ops || []).map((op: any) => {
        if (typeof op.insert === 'string' && op.insert === '\n') {
          return { ...op, attributes: { ...(op.attributes || {}), blockStyle: style } };
        }
        return op;
      });
      return new Delta(ops);
    };

    // Preserve inline styles
    clipboard.addMatcher('span', (node: HTMLElement, delta: any) => {
      const style = node.getAttribute('style');
      return applyInlineStyle(delta, style);
    });

    // Preserve block-level styles
    ['p', 'div', 'section', 'article', 'header', 'footer', 'aside', 'main', 'figure', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].forEach(tagName => {
      clipboard.addMatcher(tagName, (node: HTMLElement, delta: any) => {
        const style = node.getAttribute('style');
        return applyBlockStyle(delta, style);
      });
    });

    // Preserve iframes
    clipboard.addMatcher('iframe', (node: HTMLElement) => {
      const value = IframeBlot.value(node);
      return new Delta().insert({ iframe: value }).insert('\n');
    });

    // Preserve link target and ignore-inapp-browser attributes on paste
    clipboard.addMatcher('a', (node: HTMLElement, delta: any) => {
      const target = (node.getAttribute('target') || '').trim();
      const ignoreAttr = (node.getAttribute('ignore-inapp-browser') || '').trim();

      const allowedTargets = ['_blank', '_self', '_parent', '_top', '_newtab'];
      const normTarget = target ? (target.startsWith('_') ? target : `_${target.toLowerCase()}`) : '';
      const finalTarget = allowedTargets.includes(normTarget) ? normTarget : null;

      const finalIgnore = ignoreAttr === 'true' || ignoreAttr === 'false' ? ignoreAttr : null;

      if (!finalTarget && !finalIgnore) return delta;

      const ops = (delta.ops || []).map((op: any) => {
        if (op.attributes && op.attributes.link) {
          const newAttrs: any = { ...op.attributes };
          if (finalTarget) newAttrs.target = finalTarget;
          if (finalIgnore) newAttrs['ignore-inapp-browser'] = finalIgnore;
          return { ...op, attributes: newAttrs };
        }
        return op;
      });
      return new Delta(ops);
    });

    // Link handler delegates to provided dialog
    toolbar.addHandler('link', function (value: any) {
      if (!value) {
        this.quill.format('link', false);
      }

      const range = this.quill.getSelection(true) || { index: 0 };
      const text = this.quill.getText(range);

      Promise.resolve(opts.openLinkDialog(text)).then(result => {
        if (!result || !result.value) {
          this.quill.setSelection(range.index, range.length);
          this.quill.format('link', false);
          return;
        }

        const href = result.value.replace(/\s/g, '');
        if ((!text && !result.title) || !href) {
          this.quill.format('link', false);
          this.quill.setSelection(range.index, range.length);
          return;
        }

        if (text !== result.title) {
          this.quill.deleteText(range.index, range.length);
          this.quill.insertText(range.index, result.title);
          this.quill.formatText(range.index, result.title.length, 'link', href, 'user');
        } else {
          this.quill.formatText(range.index, range.length, 'link', href, 'user');
        }

        this.quill.setSelection(range.index, result.title.length);
      });
    });

    // Image handler keeps embed URL input behavior
    toolbar.addHandler('image', function () {
      const tooltip = this.quill.theme.tooltip;
      const originalSave = tooltip.save;
      const originalHide = tooltip.hide;

      tooltip.save = function () {
        const range = this.quill.getSelection(true);
        const value = this.textbox.value;
        if (value) {
          this.quill.insertEmbed(range.index, 'image', value, 'user');
        }
      };
      tooltip.hide = function () {
        tooltip.save = originalSave;
        tooltip.hide = originalHide;
        tooltip.hide();
      };
      tooltip.edit('image');
      tooltip.textbox.placeholder = 'Embed URL';
    });

    // Attach auto resize + fullscreen scaling reactions
    this.attachAutoResize(quill);
    this.observeFullscreen(quill);
  }

  static getQuill(): typeof Quill {
    return Quill;
  }

  private static adjustHeights(quill: any): void {
    try {
      const container = quill?.container as HTMLElement;
      if (!container) return;
      const host = container.parentElement as HTMLElement;
      if (!host) return;

      const toolbarEl = host.querySelector(':scope > .ql-toolbar') as HTMLElement | null;
      const toolbarH = toolbarEl?.offsetHeight || 0;
      const wrapper = host.closest('.description-wrapper') as HTMLElement | null;
      const isFullscreen = !!wrapper?.classList.contains('fullscreen');

      if (!isFullscreen) {
        // Reset any explicit heights in normal mode so layout is natural
        container.style.height = '';
        (quill.root as HTMLElement).style.height = '';
        return;
      }

      // In fullscreen, fill remaining space below toolbar
      const available = host.clientHeight - toolbarH;
      if (available > 0) {
        container.style.height = `${available}px`;
        (quill.root as HTMLElement).style.height = '100%';
      }
    } catch (_) { /* no-op */ }
  }

  private static observeFullscreen(quill: any): void {
    const container = quill?.container as HTMLElement;
    if (!container) return;
    const host = container.parentElement as HTMLElement;
    const wrapper = host?.closest('.description-wrapper') as HTMLElement | null;
    if (!wrapper) return;

    const apply = () => {
      const isFull = wrapper.classList.contains('fullscreen');
      this.setFullscreenScale(quill, isFull, 1.1);
      this.adjustHeights(quill);
    };

    // Initial
    apply();

    const mo = new MutationObserver(() => apply());
    mo.observe(wrapper, { attributes: true, attributeFilter: ['class'] });

    const ResizeObs = (window as any).ResizeObserver;
    if (typeof ResizeObs === 'function') {
      const ro = new ResizeObs(() => this.adjustHeights(quill));
      ro.observe(wrapper);
      ro.observe(host);
      ro.observe(container);
    } else {
      window.addEventListener('resize', () => this.adjustHeights(quill));
    }
  }

  static attachAutoResize(quill: any): void {
    // Public helper if caller wants manual attach
    this.adjustHeights(quill);
    setTimeout(() => this.adjustHeights(quill));
  }

  static setFullscreenScale(quill: any, enabled: boolean, scale = 1.1): void {
    try {
      const root = quill?.root as HTMLElement;
      if (!root) return;

      // We scale by adjusting base font-size to keep layout stable
      const ds = root.dataset as any;
      if (enabled) {
        if (!ds.originalFontSizePx) {
          const cs = getComputedStyle(root);
          const fontSize = parseFloat(cs.fontSize || '14');
          ds.originalFontSizePx = String(fontSize);
        }
        const base = parseFloat(ds.originalFontSizePx || '14');
        root.style.fontSize = `${base * scale}px`;
      } else {
        if (ds.originalFontSizePx) {
          root.style.fontSize = `${ds.originalFontSizePx}px`;
        } else {
          root.style.fontSize = '';
        }
      }
    } catch (_) { /* no-op */ }
  }
}

export default Quill;
